import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faSearch } from '@fortawesome/free-solid-svg-icons';

const AssignInterviewersModal = ({ 
  show, 
  onClose, 
  quotaProjectId, 
  quotaCellIds, // Array for bulk, single id for single
  targetCounts, // Array of target counts for each cell (for bulk)
  onSuccess,
  currentSupervisor, // Optional: the current supervisor for self-assignment
  existingAssignments // { interviewer_assignments: [...], supervisor_assignments: [...] }
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingInterviewers, setLoadingInterviewers] = useState(true);
  const [interviewers, setInterviewers] = useState([]);
  const [selectedPeople, setSelectedPeople] = useState([]); // [{type: 'interviewer'|'supervisor', id: number, completes: number}]
  const [searchTerm, setSearchTerm] = useState('');
  const [assignments, setAssignments] = useState({}); // {'interviewer_1': {assigned_count, notes, completes}, 'supervisor_5': {...}}
  const [distributeEvenly, setDistributeEvenly] = useState(true);
  const [errors, setErrors] = useState({});

  const isBulk = Array.isArray(quotaCellIds) && quotaCellIds.length > 1;
  const singleCellId = Array.isArray(quotaCellIds) ? quotaCellIds[0] : quotaCellIds;

  // Helper to get completes for a person (from existing assignments)
  const getExistingCompletes = (type, id) => {
    if (!existingAssignments) return 0;
    
    if (type === 'interviewer') {
      const found = existingAssignments.interviewer_assignments?.find(a => a.interviewer?.id === id);
      return found?.completes || 0;
    } else {
      const found = existingAssignments.supervisor_assignments?.find(a => a.supervisor?.id === id);
      return found?.completes || 0;
    }
  };

  // Helper to get existing assignment for a person
  const getExistingAssignment = (type, id) => {
    if (!existingAssignments) return null;
    
    if (type === 'interviewer') {
      return existingAssignments.interviewer_assignments?.find(a => a.interviewer?.id === id);
    } else {
      return existingAssignments.supervisor_assignments?.find(a => a.supervisor?.id === id);
    }
  };

  useEffect(() => {
    if (show) {
      fetchInterviewers();
      // Pre-populate with existing assignments (for both single and bulk modes)
      if (existingAssignments) {
        initializeFromExisting();
      }
    }
  }, [show]);

  // Initialize selected people and assignments from existing data
  const initializeFromExisting = () => {
    const newSelectedPeople = [];
    const newAssignments = {};
    
    // Add existing interviewer assignments
    existingAssignments.interviewer_assignments?.forEach(a => {
      if (a.interviewer) {
        const key = getPersonKey('interviewer', a.interviewer.id);
        newSelectedPeople.push({ 
          type: 'interviewer', 
          id: a.interviewer.id, 
          completes: a.completes || 0 
        });
        newAssignments[key] = {
          assigned_count: a.assigned_count || 0,
          notes: a.notes || '',
          completes: a.completes || 0,
          minAssigned: a.completes || 0 // Can't go below completes
        };
      }
    });
    
    // Add existing supervisor assignments
    existingAssignments.supervisor_assignments?.forEach(a => {
      if (a.supervisor) {
        const key = getPersonKey('supervisor', a.supervisor.id);
        newSelectedPeople.push({ 
          type: 'supervisor', 
          id: a.supervisor.id,
          completes: a.completes || 0
        });
        newAssignments[key] = {
          assigned_count: a.assigned_count || 0,
          notes: a.notes || '',
          completes: a.completes || 0,
          minAssigned: a.completes || 0 // Can't go below completes
        };
      }
    });
    
    if (newSelectedPeople.length > 0) {
      setSelectedPeople(newSelectedPeople);
      setAssignments(newAssignments);
      setDistributeEvenly(false); // When editing, default to custom distribution
    }
  };

  const fetchInterviewers = async () => {
    try {
      setLoadingInterviewers(true);
      const response = await axios.get('/interviewers', {
        params: {
          exclude_suspended: true
        }
      });
      if (response.data.success) {
        setInterviewers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching interviewers:', error);
    } finally {
      setLoadingInterviewers(false);
    }
  };

  // Helper to create a unique key for a person
  const getPersonKey = (type, id) => `${type}_${id}`;
  
  // Helper to check if a person is selected
  const isPersonSelected = (type, id) => {
    return selectedPeople.some(p => p.type === type && p.id === id);
  };

  // Filter interviewers based on search
  const filteredInterviewers = interviewers.filter(interviewer => 
    interviewer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    interviewer.ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    interviewer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if supervisor matches search
  const supervisorMatchesSearch = currentSupervisor && (
    !searchTerm ||
    currentSupervisor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currentSupervisor.ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currentSupervisor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePersonToggle = (type, id) => {
    const key = getPersonKey(type, id);
    const existingAssignment = getExistingAssignment(type, id);
    const completes = existingAssignment?.completes || 0;
    
    setSelectedPeople(prev => {
      const isSelected = prev.some(p => p.type === type && p.id === id);
      
      if (isSelected) {
        // Can only remove if they have 0 completes
        if (completes > 0) {
          setErrors(prevErrors => ({
            ...prevErrors,
            [key]: `Cette personne a ${completes} complétion(s), impossible de la retirer`
          }));
          return prev; // Don't remove
        }
        
        // Remove person and their assignment
        const newAssignments = { ...assignments };
        delete newAssignments[key];
        setAssignments(newAssignments);
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[key];
          return newErrors;
        });
        return prev.filter(p => !(p.type === type && p.id === id));
      } else {
        // Add person
        const newPeople = [...prev, { type, id, completes }];
        
        // Recalculate assignments if even distribution
        if (distributeEvenly && !isBulk) {
          recalculateDistribution(newPeople);
        } else {
          // Initialize with existing or 0
          const existingCount = existingAssignment?.assigned_count || 0;
          setAssignments(prevA => ({
            ...prevA,
            [key]: { 
              assigned_count: existingCount, 
              notes: existingAssignment?.notes || '',
              completes: completes,
              minAssigned: completes // Can't go below completes
            }
          }));
        }
        
        return newPeople;
      }
    });
  };

  const handleAssignmentChange = (key, field, value) => {
    setAssignments(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: field === 'assigned_count' ? parseInt(value) || 0 : value
      }
    }));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  };

  const recalculateDistribution = (people = selectedPeople) => {
    if (isBulk || people.length === 0) return;
    
    const targetCount = Array.isArray(targetCounts) ? targetCounts[0] : targetCounts;
    const countPerPerson = Math.floor(targetCount / people.length);
    const remainder = targetCount % people.length;
    
    const newAssignments = {};
    people.forEach((person, index) => {
      const key = getPersonKey(person.type, person.id);
      newAssignments[key] = {
        assigned_count: countPerPerson + (index < remainder ? 1 : 0),
        notes: assignments[key]?.notes || ''
      };
    });
    setAssignments(newAssignments);
  };

  useEffect(() => {
    if (distributeEvenly && !isBulk && selectedPeople.length > 0) {
      recalculateDistribution();
    }
  }, [distributeEvenly, selectedPeople.length, isBulk]);

  const validateAssignments = () => {
    const newErrors = {};

    if (!isBulk && selectedPeople.length > 0) {
      const targetCount = Array.isArray(targetCounts) ? targetCounts[0] : targetCounts;
      const totalAssigned = selectedPeople.reduce((sum, person) => {
        const key = getPersonKey(person.type, person.id);
        return sum + (assignments[key]?.assigned_count || 0);
      }, 0);

      if (totalAssigned > targetCount) {
        newErrors.total = `Le total assigné (${totalAssigned}) dépasse le quota cible (${targetCount})`;
      }

      // Check each person's assignment is not below their completes
      selectedPeople.forEach(person => {
        const key = getPersonKey(person.type, person.id);
        const assignment = assignments[key];
        const minAssigned = assignment?.minAssigned || assignment?.completes || 0;
        const assignedCount = assignment?.assigned_count || 0;
        if (minAssigned > 0 && assignedCount < minAssigned) {
          newErrors[key] = `Minimum ${minAssigned} (complétés)`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedPeople.length === 0) {
      setErrors({ general: 'Veuillez sélectionner au moins une personne' });
      return;
    }

    if (!validateAssignments()) {
      return;
    }

    try {
      setLoading(true);
      
      // Separate interviewers and supervisor
      const selectedInterviewerIds = selectedPeople
        .filter(p => p.type === 'interviewer')
        .map(p => p.id);
      const selectedSupervisor = selectedPeople.find(p => p.type === 'supervisor');
      const supervisorId = selectedSupervisor?.id || null;
      
      if (isBulk) {
        // Bulk assignment
        await axios.post(`/quotas/projects/${quotaProjectId}/bulk-assign`, {
          quota_cell_ids: quotaCellIds,
          interviewer_ids: selectedInterviewerIds,
          supervisor_id: supervisorId,
          distribute_evenly: distributeEvenly
        });
      } else {
        // Single cell assignment
        const interviewerAssignments = selectedPeople
          .filter(p => p.type === 'interviewer')
          .map(p => {
            const key = getPersonKey(p.type, p.id);
            return {
              interviewer_id: p.id,
              assigned_count: assignments[key]?.assigned_count || 0,
              notes: assignments[key]?.notes || ''
            };
          });
        
        let supervisorAssignment = null;
        if (supervisorId) {
          const key = getPersonKey('supervisor', supervisorId);
          supervisorAssignment = {
            supervisor_id: supervisorId,
            assigned_count: assignments[key]?.assigned_count || 0,
            notes: assignments[key]?.notes || ''
          };
        }
        
        const assignmentData = distributeEvenly ? {
          interviewer_ids: selectedInterviewerIds,
          supervisor_id: supervisorId,
          distribute_evenly: true
        } : {
          interviewer_ids: selectedInterviewerIds,
          supervisor_id: supervisorId,
          distribute_evenly: false,
          assignments: interviewerAssignments,
          supervisor_assignment: supervisorAssignment
        };
        
        await axios.post(`/quotas/projects/${quotaProjectId}/cells/${singleCellId}/assignments`, assignmentData);
      }

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      console.error('Error assigning:', error);
      setErrors({ 
        general: error.response?.data?.message || 'Erreur lors de l\'assignation'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPeople([]);
    setAssignments({});
    setSearchTerm('');
    setErrors({});
    setDistributeEvenly(true);
    onClose();
  };

  // Editable assignment form: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(handleClose, { backdropClose: false, enabled: show });

  if (!show) return null;

  const targetCount = Array.isArray(targetCounts) ? targetCounts[0] : targetCounts;
  const totalAssigned = selectedPeople.reduce((sum, person) => {
    const key = getPersonKey(person.type, person.id);
    return sum + (assignments[key]?.assigned_count || 0);
  }, 0);

  // Get person info for display in custom distribution
  const getPersonInfo = (person) => {
    if (person.type === 'supervisor') {
      return currentSupervisor;
    }
    return interviewers.find(i => i.id === person.id);
  };

  return (
    <div className="modal-overlay" {...dismiss}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {isBulk 
              ? `Assigner des personnes (${quotaCellIds.length} quotas)`
              : 'Assigner des personnes au quota'
            }
          </h2>
          <button className="btn-icon" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Even distribution checkbox */}
          <div className="form-group" style={{ marginTop: '10px' }}>
            <label style={{ marginBottom: '6px', display: 'block', fontSize: '14px', fontWeight: 500, color: '#333' }}>
              <input
                type="checkbox"
                checked={distributeEvenly}
                onChange={(e) => setDistributeEvenly(e.target.checked)}
                style={{ marginRight: '8px', cursor: 'pointer', width: 'auto' }}
              />
              Distribuer équitablement
            </label>
            {!isBulk && distributeEvenly && targetCount && selectedPeople.length > 0 && (() => {
              const countPerPerson = Math.floor(targetCount / selectedPeople.length);
              const remainder = targetCount % selectedPeople.length;
              const totalDistributed = countPerPerson * selectedPeople.length + remainder;
              
              return (
                <div className="info-message" style={{ marginTop: '10px' }}>
                  <p>
                    Distribution: {selectedPeople.length} personne(s) pour {targetCount} quotas.
                    {remainder === 0 ? (
                      ` Chaque personne recevra exactement ${countPerPerson} quotas.`
                    ) : (
                      ` Les ${remainder} première(s) personne(s) recevront ${countPerPerson + 1} quotas, les autres ${countPerPerson} quotas. (Total: ${totalDistributed} quotas distribués)`
                    )}
                  </p>
                </div>
              );
            })()}
          </div>

          {/* Search */}
          <div className="form-group">
            <label>Rechercher</label>
            <div style={{ position: 'relative' }}>
              <FontAwesomeIcon 
                icon={faSearch} 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} 
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nom, référence ou email..."
                style={{ paddingLeft: '35px' }}
              />
            </div>
          </div>

          {/* People List (Supervisor + Interviewers) */}
          <div className="form-group">
            <label>Sélectionner les personnes à assigner *</label>
            {loadingInterviewers ? (
              <div className="text-center py-4">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                <p>Chargement...</p>
              </div>
            ) : (
              <div style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                maxHeight: '350px', 
                overflowY: 'auto' 
              }}>
                {/* Current Supervisor (shown first, with special styling) */}
                {currentSupervisor && supervisorMatchesSearch && (() => {
                  const supExisting = getExistingAssignment('supervisor', currentSupervisor.id);
                  const supCompletes = supExisting?.completes || 0;
                  const supAssigned = supExisting?.assigned_count || 0;
                  
                  return (
                    <div
                      style={{
                        padding: '12px',
                        borderBottom: '2px solid #00796b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: supCompletes > 0 && isPersonSelected('supervisor', currentSupervisor.id) ? 'not-allowed' : 'pointer',
                        backgroundColor: isPersonSelected('supervisor', currentSupervisor.id) ? '#e0f7fa' : '#f0fdfa'
                      }}
                      onClick={() => handlePersonToggle('supervisor', currentSupervisor.id)}
                    >
                      <input
                        type="checkbox"
                        checked={isPersonSelected('supervisor', currentSupervisor.id)}
                        onChange={() => handlePersonToggle('supervisor', currentSupervisor.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={supCompletes > 0 && isPersonSelected('supervisor', currentSupervisor.id)}
                        style={{ flexShrink: 0, width: '18px', height: '18px', cursor: supCompletes > 0 ? 'not-allowed' : 'pointer' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, color: '#00796b' }}>{currentSupervisor.name}</span>
                          <span
                            style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: '#00796b',
                              color: 'white',
                              fontWeight: 500,
                              flexShrink: 0
                            }}
                          >
                            MOI (Prestataire)
                          </span>
                          {supAssigned > 0 && (
                            <span
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor: '#dbeafe',
                                color: '#1e40af',
                                fontWeight: 500
                              }}
                            >
                              {supCompletes}/{supAssigned} fait
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {currentSupervisor.ref && `Ref: ${currentSupervisor.ref}`}
                          {currentSupervisor.ref && currentSupervisor.email && ' • '}
                          {currentSupervisor.email}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Interviewers */}
                {filteredInterviewers.length === 0 && !supervisorMatchesSearch ? (
                  <div className="text-center py-4 text-gray-500">
                    Aucune personne trouvée
                  </div>
                ) : (
                  filteredInterviewers.map(interviewer => {
                    const statusColors = {
                      active: { bg: '#d1fae5', text: '#065f46', label: 'Actif' },
                      inactive: { bg: '#fef3c7', text: '#92400e', label: 'Inactif' },
                      suspended: { bg: '#fee2e2', text: '#991b1b', label: 'Suspendu' }
                    };
                    const status = statusColors[interviewer.status] || { bg: '#e5e7eb', text: '#374151', label: interviewer.status || 'N/A' };
                    
                    // Get existing assignment info
                    const intExisting = getExistingAssignment('interviewer', interviewer.id);
                    const intCompletes = intExisting?.completes || 0;
                    const intAssigned = intExisting?.assigned_count || 0;
                    const hasCompletes = intCompletes > 0 && isPersonSelected('interviewer', interviewer.id);
                    
                    return (
                      <div
                        key={interviewer.id}
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: hasCompletes ? 'not-allowed' : 'pointer',
                          backgroundColor: isPersonSelected('interviewer', interviewer.id) ? '#f0f7ff' : 'white'
                        }}
                        onClick={() => handlePersonToggle('interviewer', interviewer.id)}
                      >
                        <input
                          type="checkbox"
                          checked={isPersonSelected('interviewer', interviewer.id)}
                          onChange={() => handlePersonToggle('interviewer', interviewer.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={hasCompletes}
                          style={{ flexShrink: 0, width: '18px', height: '18px', cursor: hasCompletes ? 'not-allowed' : 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 500 }}>{interviewer.name}</span>
                            <span
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor: status.bg,
                                color: status.text,
                                fontWeight: 500,
                                textTransform: 'capitalize',
                                flexShrink: 0
                              }}
                            >
                              {status.label}
                            </span>
                            {intAssigned > 0 && (
                              <span
                                style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: intCompletes > 0 ? '#d1fae5' : '#dbeafe',
                                  color: intCompletes > 0 ? '#065f46' : '#1e40af',
                                  fontWeight: 500
                                }}
                              >
                                {intCompletes}/{intAssigned} fait
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {interviewer.ref && `Ref: ${interviewer.ref}`}
                            {interviewer.ref && interviewer.email && ' • '}
                            {interviewer.email}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Custom Distribution (when not distributing evenly) */}
          {!distributeEvenly && !isBulk && selectedPeople.length > 0 && (
            <div className="form-group">
              <label>Répartition personnalisée</label>
              <div style={{ marginBottom: '10px', fontSize: '13px', color: '#666' }}>
                Quota cible: {targetCount} | Total assigné: {totalAssigned}
                {totalAssigned > targetCount && (
                  <span style={{ color: '#dc3545', marginLeft: '10px' }}>
                    ⚠ Dépassement de {totalAssigned - targetCount}
                  </span>
                )}
              </div>
              
              {selectedPeople.map(person => {
                const key = getPersonKey(person.type, person.id);
                const personInfo = getPersonInfo(person);
                const assignment = assignments[key] || { assigned_count: 0, notes: '', completes: 0, minAssigned: 0 };
                const isSupervisor = person.type === 'supervisor';
                const completes = assignment.completes || 0;
                const minAssigned = assignment.minAssigned || completes || 0;
                const remaining = (assignment.assigned_count || 0) - completes;
                
                return (
                  <div key={key} style={{ 
                    marginBottom: '15px', 
                    padding: '12px', 
                    border: isSupervisor ? '1px solid #00796b' : '1px solid #e0e0e0', 
                    borderRadius: '4px',
                    backgroundColor: isSupervisor ? '#e0f7fa' : 'white'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontWeight: 500, color: isSupervisor ? '#00796b' : '#333' }}>
                        {personInfo?.name} ({personInfo?.ref})
                        {isSupervisor && <span style={{ marginLeft: '8px', fontSize: '12px' }}>(Moi)</span>}
                      </div>
                      {completes > 0 && (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          fontSize: '12px'
                        }}>
                          <span style={{ 
                            padding: '2px 8px', 
                            borderRadius: '12px', 
                            backgroundColor: '#d1fae5', 
                            color: '#065f46',
                            fontWeight: 500
                          }}>
                            ✓ {completes} terminé(s)
                          </span>
                          {remaining > 0 && (
                            <span style={{ 
                              padding: '2px 8px', 
                              borderRadius: '12px', 
                              backgroundColor: '#fef3c7', 
                              color: '#92400e',
                              fontWeight: 500
                            }}>
                              {remaining} restant(s)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          Nombre de quotas {minAssigned > 0 && <span style={{ color: '#666' }}>(min: {minAssigned})</span>}
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={targetCount}
                          value={assignment.assigned_count}
                          onChange={(e) => {
                            handleAssignmentChange(key, 'assigned_count', e.target.value);
                            const newValue = parseInt(e.target.value) || 0;
                            if (newValue < minAssigned) {
                              setErrors(prev => ({
                                ...prev,
                                [key]: `Minimum ${minAssigned} (complétés)`
                              }));
                            } else {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[key];
                                return newErrors;
                              });
                            }
                          }}
                          style={{ 
                            width: '100%',
                            borderColor: errors[key] ? '#dc3545' : undefined
                          }}
                        />
                        {errors[key] && (
                          <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>
                            {errors[key]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Error messages */}
          {errors.general && (
            <div className="form-group">
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#fee', 
                color: '#c33', 
                borderRadius: '4px' 
              }}>
                {errors.general}
              </div>
            </div>
          )}

          {errors.total && (
            <div className="form-group">
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#fee', 
                color: '#c33', 
                borderRadius: '4px' 
              }}>
                {errors.total}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || selectedPeople.length === 0}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> 
                  Assignation...
                </>
              ) : (
                `Assigner (${selectedPeople.length})`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignInterviewersModal;
